'use client';

import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { authWrite, getToken } from '@/lib/auth';
import type { AuthUser, UserBootstrapData } from '@/lib/auth';
import { useAuth } from '@/components/AuthProvider';
import { getCachedWeatherWithAge, getWeatherForUser, CACHE_TTL_MS } from '@/lib/weather';
import type { WeatherData } from '@/lib/weather';
import { primeTaskCache, resetTaskCache, saveTasks as saveTasksToDataSync } from '@/lib/dataSync';
import type { TaskData, CustomTask } from '@/lib/dataSync';
import type { JournalEntry } from '@/data/journal';
import type { PlantingLog } from '@/lib/plantingTracker';
import type { HarvestEntry } from '@/lib/harvestLog';
import type { SeedPacket } from '@/lib/seedInventory';
import type { UserGardenLocation } from '@/lib/userGardenLocation';
import { normalizeUserGardenLocation, saveStoredUserGardenLocation } from '@/lib/userGardenLocation';
import type { AlmanacData } from '@/lib/almanac';
import { logPlantingLogNormalizationDiagnostics, normalizePlantingLogs } from '@/lib/plantingLogNormalization';
import {
  logTaskStateHygieneDiagnostics,
  pruneOrphanedAutoTaskState,
  pruneOrphanedTaskRecurrence,
  toAutoTaskState,
} from '@/lib/guidance/taskStateHygiene';

// ── Interface ─────────────────────────────────────────────────────────────────

interface UserData {
  // Tasks
  tasks: TaskData;
  saveTasks: (tasks: TaskData) => Promise<void>;

  // Journal
  journalEntries: JournalEntry[];
  saveJournalEntries: (entries: JournalEntry[]) => Promise<void>;

  // Planting logs
  plantingLogs: PlantingLog[];
  savePlantingLogs: (logs: PlantingLog[]) => Promise<void>;

  // Harvest
  harvestEntries: HarvestEntry[];
  saveHarvestEntries: (entries: HarvestEntry[]) => Promise<void>;

  // Seed inventory
  seedInventory: SeedPacket[];
  saveSeedInventory: (packets: SeedPacket[] | ((current: SeedPacket[]) => SeedPacket[])) => Promise<void>;

  // Planned plants
  plannedPlants: string[];
  savePlannedPlants: (ids: string[] | ((current: string[]) => string[])) => Promise<void>;

  // Favorites
  favorites: string[];
  saveFavorites: (ids: string[] | ((current: string[]) => string[])) => Promise<void>;

  // Zone and weather
  zone: number | null;
  zip: string | null;
  saveZone: (zone: number, zip: string, gardenLocation?: UserGardenLocation | null) => Promise<void>;
  weatherData: WeatherData | null;

  // Almanac (display-only reference data, lazily loaded — never part of bootstrap)
  almanacData: AlmanacData | null;
  almanacLoading: boolean;
  loadAlmanac: () => Promise<void>;

  // Loading state
  isLoading: boolean;
  isLoaded: boolean;
  isFresh: boolean;

  // Refresh
  refreshAll: () => Promise<void>;
}

// ── Context ───────────────────────────────────────────────────────────────────

const UserDataContext = createContext<UserData | null>(null);

declare global {
  interface Window {
    __gardenDebugPlantingLog?: (logId: string) => Promise<void>;
  }
}

type StateUpdater<T> = T | ((current: T) => T);

function resolveNextState<T>(nextOrUpdater: StateUpdater<T>, current: T): T {
  return typeof nextOrUpdater === 'function'
    ? (nextOrUpdater as (current: T) => T)(current)
    : nextOrUpdater;
}

function mergeTaskData(current: TaskData, updates: TaskData): TaskData {
  return {
    checked: updates.checked ?? current.checked ?? [],
    custom: updates.custom ?? current.custom ?? [],
    autoChecked: updates.autoChecked ?? current.autoChecked ?? [],
    autoDismissed: updates.autoDismissed ?? current.autoDismissed ?? [],
    autoOverrides: updates.autoOverrides ?? current.autoOverrides ?? {},
    autoArchived: updates.autoArchived ?? current.autoArchived ?? {},
  };
}

function clearLocalGuidanceMemory(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('garden-task-recurrence-v1');
  localStorage.removeItem('garden-guidance-shown-v1');
  localStorage.removeItem('garden-planting-timing-dismissed');
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function UserDataProvider({ children }: { children: ReactNode }) {
  const {
    user,
    loading: authLoading,
    bootstrapData,
    bootstrapLoading,
  } = useAuth();

  // All state is initialized lazily from localStorage so the UI renders
  // instantly with cached data before the KV background refresh completes.

  const [tasks, setTasks] = useState<TaskData>(() => {
    if (typeof window === 'undefined') return { checked: [], custom: [] };
    try {
      const raw = localStorage.getItem('garden-tasks');
      if (!raw) return { checked: [], custom: [] };
      const t = JSON.parse(raw);
      return {
        checked: t.checked ?? [],
        custom: t.custom ?? [],
        autoChecked: t.autoChecked ?? [],
        autoDismissed: t.autoDismissed ?? [],
        autoOverrides: t.autoOverrides ?? {},
        autoArchived: t.autoArchived ?? {},
      };
    } catch { return { checked: [], custom: [] }; }
  });
  const tasksRef = useRef(tasks);

  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = localStorage.getItem('garden-journal-entries');
      return raw ? (JSON.parse(raw) as JournalEntry[]) : [];
    } catch { return []; }
  });

  const [plantingLogs, setPlantingLogs] = useState<PlantingLog[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = localStorage.getItem('garden-planting-logs');
      return raw ? normalizePlantingLogs(JSON.parse(raw) as PlantingLog[]).logs : [];
    } catch { return []; }
  });

  const [harvestEntries, setHarvestEntries] = useState<HarvestEntry[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = localStorage.getItem('garden-harvest-log');
      return raw ? (JSON.parse(raw) as HarvestEntry[]) : [];
    } catch { return []; }
  });

  const [seedInventory, setSeedInventory] = useState<SeedPacket[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = localStorage.getItem('garden-seed-inventory');
      return raw ? (JSON.parse(raw) as SeedPacket[]) : [];
    } catch { return []; }
  });

  const [plannedPlants, setPlannedPlants] = useState<string[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = localStorage.getItem('garden-planned-plants');
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch { return []; }
  });

  const [favorites, setFavorites] = useState<string[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = localStorage.getItem('garden_favorites');
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch { return []; }
  });

  const [zone, setZone] = useState<number | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = localStorage.getItem('garden-user-zone');
      if (!raw) return null;
      const zd = JSON.parse(raw) as { zone?: number };
      return zd.zone ?? null;
    } catch { return null; }
  });

  const [zip, setZip] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const dedicated = localStorage.getItem('garden-zone-zip');
      if (dedicated) return dedicated;
      const raw = localStorage.getItem('garden-user-zone');
      if (!raw) return null;
      const zd = JSON.parse(raw) as { zip?: string };
      return zd.zip ?? null;
    } catch { return null; }
  });

  const [almanacData, setAlmanacData] = useState<AlmanacData | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = localStorage.getItem('garden-almanac');
      return raw ? JSON.parse(raw) as AlmanacData : null;
    } catch { return null; }
  });
  const [almanacLoading, setAlmanacLoading] = useState(false);

  const [weatherData, setWeatherData] = useState<WeatherData | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const expectedZip =
        localStorage.getItem('garden-zone-zip')
        ?? (() => {
          const raw = localStorage.getItem('garden-user-zone');
          if (!raw) return null;
          const zoneData = JSON.parse(raw) as { zip?: string };
          return zoneData.zip ?? null;
        })();
      const cached = getCachedWeatherWithAge(expectedZip);
      if (!cached) return null;
      const ageMs = Date.now() - cached.fetchedAt.getTime();
      if (ageMs < CACHE_TTL_MS) return cached.data;
      return null;
    } catch { return null; }
  });

  // isLoaded is true immediately if localStorage has any data
  const [isLoaded, setIsLoaded] = useState(() => {
    if (typeof window === 'undefined') return false;
    return !!(
      localStorage.getItem('garden-tasks') ||
      localStorage.getItem('garden-journal-entries') ||
      localStorage.getItem('garden-planting-logs') ||
      localStorage.getItem('garden-planned-plants')
    );
  });

  // isFresh = false means showing cache; true means live KV data has arrived
  const [isFresh, setIsFresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const lastUserEmailRef = useRef<string | null>(null);
  const seedInventoryRef = useRef(seedInventory);
  const plannedPlantsRef = useRef(plannedPlants);
  const favoritesRef = useRef(favorites);
  const normalizedLogSaveSignatureRef = useRef<string | null>(null);
  const taskStatePruneSaveSignatureRef = useRef<string | null>(null);
  const localTaskHygieneRanRef = useRef(false);

  const clearInMemoryUserState = useCallback(() => {
    const emptyTasks: TaskData = {
      checked: [],
      custom: [],
      autoChecked: [],
      autoDismissed: [],
      autoOverrides: {},
      autoArchived: {},
    };
    setTasks(emptyTasks);
    tasksRef.current = emptyTasks;
    resetTaskCache();
    setJournalEntries([]);
    setPlantingLogs([]);
    setHarvestEntries([]);
    setSeedInventory([]);
    seedInventoryRef.current = [];
    setPlannedPlants([]);
    plannedPlantsRef.current = [];
    setFavorites([]);
    favoritesRef.current = [];
    setZone(null);
    setZip(null);
    setWeatherData(null);
    setAlmanacData(null);
  }, []);

  useEffect(() => {
    tasksRef.current = tasks;
  }, [tasks]);

  useEffect(() => {
    seedInventoryRef.current = seedInventory;
  }, [seedInventory]);

  useEffect(() => {
    plannedPlantsRef.current = plannedPlants;
  }, [plannedPlants]);

  useEffect(() => {
    favoritesRef.current = favorites;
  }, [favorites]);

  useEffect(() => {
    if (localTaskHygieneRanRef.current || typeof window === 'undefined') return;
    if (localStorage.getItem('garden-planting-logs') === null) return;

    localTaskHygieneRanRef.current = true;
    const taskPrune = pruneTaskStateForLogs(
      'local-hydration',
      tasksRef.current,
      plantingLogs,
    );
    if (!taskPrune.changed) return;

    applyTaskStateLocally(taskPrune.taskState);
    if (getToken()) {
      savePrunedTaskStateOnce('local-hydration', taskPrune.taskState, taskPrune.removedIds);
    }
  }, [plantingLogs]);

  // ── KV sync helper ────────────────────────────────────────────────────────

  function guardFreshWrite() {
    const token = getToken();
    if (!token || isFresh) return true;

    window.dispatchEvent(
      new CustomEvent('garden:sync-error', {
        detail: {
          message: 'Syncing your garden data. Please wait a moment and try again.',
        },
      }),
    );
    return false;
  }

  async function syncToKV(endpoint: string, data: unknown) {
    const token = getToken();
    if (!token) return;
    await authWrite(
      endpoint,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
      'Your changes were saved locally, but could not be synced.',
    );
  }

  function applyTaskStateLocally(nextTasks: TaskData) {
    tasksRef.current = nextTasks;
    setTasks(nextTasks);
    primeTaskCache(nextTasks);
    try { localStorage.setItem('garden-tasks', JSON.stringify(nextTasks)); } catch {}
  }

  function pruneTaskStateForLogs(label: string, taskState: TaskData, logs: PlantingLog[]) {
    const taskPrune = pruneOrphanedAutoTaskState(taskState, logs);
    const recurrencePrune = pruneOrphanedTaskRecurrence(logs);
    logTaskStateHygieneDiagnostics(
      label,
      logs,
      taskPrune.taskState,
      taskPrune,
      recurrencePrune,
    );
    return taskPrune;
  }

  function savePrunedTaskStateOnce(label: string, taskState: TaskData, removedIds: string[]) {
    if (removedIds.length === 0) return;
    const signature = JSON.stringify([label, ...removedIds].sort());
    if (taskStatePruneSaveSignatureRef.current === signature) return;
    taskStatePruneSaveSignatureRef.current = signature;
    void saveTasksToDataSync(taskState);
  }

  // ── Bootstrap hydration ───────────────────────────────────────────────────
  // Runs after the initial render; updates state silently with fresh KV data.

  const applyBootstrapData = useCallback((data: UserBootstrapData, bootstrapUser: AuthUser | null) => {
    const normalizedPlantingLogs = normalizePlantingLogs(data.plantingLogs);
    logPlantingLogNormalizationDiagnostics(normalizedPlantingLogs);

    const rawTaskState: TaskData = {
      checked: data.tasks.checked ?? [],
      custom: (data.tasks.custom ?? []) as CustomTask[],
      autoChecked: data.tasks.autoChecked ?? [],
      autoDismissed: data.tasks.autoDismissed ?? [],
      autoOverrides: data.tasks.autoOverrides ?? {},
      autoArchived: data.tasks.autoArchived ?? {},
    };
    const taskPrune = pruneTaskStateForLogs(
      'bootstrap',
      rawTaskState,
      normalizedPlantingLogs.logs,
    );
    const fullTaskState = taskPrune.taskState;
    applyTaskStateLocally(fullTaskState);
    if (taskPrune.changed) {
      savePrunedTaskStateOnce('bootstrap', fullTaskState, taskPrune.removedIds);
    }

    setJournalEntries(data.journalEntries);
    try { localStorage.setItem('garden-journal-entries', JSON.stringify(data.journalEntries)); } catch {}

    setPlantingLogs(normalizedPlantingLogs.logs);
    try { localStorage.setItem('garden-planting-logs', JSON.stringify(normalizedPlantingLogs.logs)); } catch {}
    if (normalizedPlantingLogs.changed) {
      const signature = JSON.stringify(normalizedPlantingLogs.repairs.map(repair => [
        repair.logId,
        repair.oldPlantId ?? '',
        repair.newPlantId,
      ]));
      if (normalizedLogSaveSignatureRef.current !== signature) {
        normalizedLogSaveSignatureRef.current = signature;
        void syncToKV('/api/user/planting-logs', { logs: normalizedPlantingLogs.logs });
      }
    }

    setHarvestEntries(data.harvestEntries);
    try { localStorage.setItem('garden-harvest-log', JSON.stringify(data.harvestEntries)); } catch {}

    setFavorites(data.favorites);
    favoritesRef.current = data.favorites;
    try { localStorage.setItem('garden_favorites', JSON.stringify(data.favorites)); } catch {}

    setSeedInventory(data.seedInventory);
    seedInventoryRef.current = data.seedInventory;
    try { localStorage.setItem('garden-seed-inventory', JSON.stringify(data.seedInventory)); } catch {}

    setPlannedPlants(data.plannedPlants);
    plannedPlantsRef.current = data.plannedPlants;
    try { localStorage.setItem('garden-planned-plants', JSON.stringify(data.plannedPlants)); } catch {}

    const userZone = bootstrapUser?.zone ?? null;
    const userZip = bootstrapUser?.zip ?? null;
    const userGardenLocation = normalizeUserGardenLocation(bootstrapUser?.gardenLocation);
    if (userZone !== null) {
      setZone(userZone);
      try {
        localStorage.setItem('garden-user-zone', JSON.stringify({
          zone: userZone,
          zip: userZip,
          ...(userGardenLocation ? { gardenLocation: userGardenLocation } : {}),
          setAt: new Date().toISOString(),
        }));
      } catch {}
    }
    saveStoredUserGardenLocation(userGardenLocation);
    if (userZip) {
      setZip(userZip);
      try { localStorage.setItem('garden-zone-zip', userZip); } catch {}
    }

    if (userZip) {
      const cachedWeather = getCachedWeatherWithAge(userZip);
      if (cachedWeather) setWeatherData(cachedWeather.data);
      getWeatherForUser({
        bypassCache: true,
        onGeocodingFailure: () => {
          window.dispatchEvent(new CustomEvent('garden:sync-error', {
            detail: { message: 'Could not find weather for your ZIP code. Re-enter your ZIP in zone settings to enable frost and watering alerts.' },
          }));
        },
      })
        .then(w => { if (w) setWeatherData(w); })
        .catch(() => {});
    }
  }, []);

  const refreshFromKV = useCallback(async () => {
    setIsLoading(true);
    const token = getToken();

    if (!token) {
      setIsLoaded(true);
      setIsFresh(true);
      setIsLoading(false);
      return;
    }

    try {
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const response = await fetch('/api/user/bootstrap', { headers });
      if (response.status === 401) {
        window.dispatchEvent(new Event('garden:auth-invalid'));
        return;
      }
      if (response.ok) {
        const payload = await response.json() as {
          success?: boolean;
          user?: AuthUser;
          data?: UserBootstrapData;
        };
        if (payload.success && payload.data) {
          applyBootstrapData(payload.data, payload.user ?? user);
        }
      }
    } catch (err) {
      console.error('UserDataContext: KV refresh failed', err);
    } finally {
      setIsLoading(false);
      setIsLoaded(true);
      setIsFresh(true);
    }
  }, [applyBootstrapData, user]);

  // Prime task cache with cached data before live bootstrap hydration arrives.
  useEffect(() => {
    try {
      const raw = localStorage.getItem('garden-tasks');
      if (raw) primeTaskCache(JSON.parse(raw));
    } catch {}
  }, []);

  useEffect(() => {
    if (authLoading || bootstrapLoading) return;

    const currentUserEmail = user?.email ?? null;
    const previousUserEmail = lastUserEmailRef.current;
    const accountChanged = previousUserEmail !== null && currentUserEmail !== previousUserEmail;
    if (accountChanged || (!user && previousUserEmail !== null)) {
      clearInMemoryUserState();
      clearLocalGuidanceMemory();
    }
    lastUserEmailRef.current = currentUserEmail;

    if (!user) {
      setIsLoaded(true);
      setIsFresh(true);
      setIsLoading(false);
      return;
    }

    if (bootstrapData) {
      setIsLoading(true);
      applyBootstrapData(bootstrapData, user);
      setIsLoaded(true);
      setIsFresh(true);
      setIsLoading(false);
      return;
    }

    setIsLoaded(false);
    setIsFresh(false);
    setIsLoading(true);
    void refreshFromKV();
  }, [
    applyBootstrapData,
    authLoading,
    bootstrapData,
    bootstrapLoading,
    clearInMemoryUserState,
    refreshFromKV,
    user,
  ]);

  useEffect(() => {
    const handleAuthInvalid = () => {
      setIsLoaded(true);
      setIsFresh(true);
      setIsLoading(false);
    };

    window.addEventListener('garden:auth-invalid', handleAuthInvalid);
    return () => window.removeEventListener('garden:auth-invalid', handleAuthInvalid);
  }, []);

  useEffect(() => {
    const handleSyncError = (event: Event) => {
      const message =
        event instanceof CustomEvent && typeof event.detail?.message === 'string'
          ? event.detail.message
          : 'Your changes were not synced.';
      setSyncError(message);
    };

    window.addEventListener('garden:sync-error', handleSyncError);
    return () => window.removeEventListener('garden:sync-error', handleSyncError);
  }, []);

  // ── Save functions ────────────────────────────────────────────────────────

  async function saveTasks(newTasks: TaskData) {
    if (!guardFreshWrite()) return;
    const mergedTasks = mergeTaskData(tasksRef.current, newTasks);
    const taskPrune = pruneTaskStateForLogs('saveTasks', mergedTasks, plantingLogs);
    const nextTasks = taskPrune.taskState;
    applyTaskStateLocally(nextTasks);
    // Use saveTasksToDataSync which merges with the module-level cache so
    // autoChecked / autoArchived fields are preserved in both localStorage and KV.
    await saveTasksToDataSync(nextTasks);
  }

  async function saveJournalEntries(entries: JournalEntry[]) {
    if (!guardFreshWrite()) return;
    setJournalEntries(entries);
    try { localStorage.setItem('garden-journal-entries', JSON.stringify(entries)); } catch {}
    try {
      await syncToKV('/api/user/journal', { entries });
    } catch {}
  }

  async function savePlantingLogs(logs: PlantingLog[]) {
    if (!guardFreshWrite()) return;
    const normalized = normalizePlantingLogs(logs);
    logPlantingLogNormalizationDiagnostics(normalized);
    setPlantingLogs(normalized.logs);
    try { localStorage.setItem('garden-planting-logs', JSON.stringify(normalized.logs)); } catch {}
    const taskPrune = pruneTaskStateForLogs(
      'savePlantingLogs',
      tasksRef.current,
      normalized.logs,
    );
    if (taskPrune.changed) {
      applyTaskStateLocally(taskPrune.taskState);
      await saveTasksToDataSync(taskPrune.taskState);
    }
    try {
      await syncToKV('/api/user/planting-logs', { logs: normalized.logs });
    } catch {}
  }

  async function saveHarvestEntries(entries: HarvestEntry[]) {
    if (!guardFreshWrite()) return;
    setHarvestEntries(entries);
    try { localStorage.setItem('garden-harvest-log', JSON.stringify(entries)); } catch {}
    try {
      await syncToKV('/api/user/harvest', { entries });
    } catch {}
  }

  async function saveSeedInventory(packets: StateUpdater<SeedPacket[]>) {
    if (!guardFreshWrite()) return;
    const nextPackets = resolveNextState(packets, seedInventoryRef.current);
    seedInventoryRef.current = nextPackets;
    setSeedInventory(nextPackets);
    try { localStorage.setItem('garden-seed-inventory', JSON.stringify(nextPackets)); } catch {}
    try {
      await syncToKV('/api/user/seeds', { packets: nextPackets });
    } catch {}
  }

  async function savePlannedPlants(ids: StateUpdater<string[]>) {
    if (!guardFreshWrite()) return;
    const nextIds = resolveNextState(ids, plannedPlantsRef.current);
    plannedPlantsRef.current = nextIds;
    setPlannedPlants(nextIds);
    try { localStorage.setItem('garden-planned-plants', JSON.stringify(nextIds)); } catch {}
    try {
      await syncToKV('/api/user/planned-plants', { plantIds: nextIds });
    } catch {}
  }

  async function saveFavorites(ids: StateUpdater<string[]>) {
    if (!guardFreshWrite()) return;
    const nextIds = resolveNextState(ids, favoritesRef.current);
    favoritesRef.current = nextIds;
    setFavorites(nextIds);
    try { localStorage.setItem('garden_favorites', JSON.stringify(nextIds)); } catch {}
    try {
      await syncToKV('/api/user/saved-plants', { plantIds: nextIds });
    } catch {}
  }

  const loadAlmanac = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    setAlmanacLoading(true);
    try {
      const res = await fetch('/api/almanac', { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const payload = await res.json() as { almanac?: AlmanacData | null };
      const next = payload.almanac ?? null;
      setAlmanacData(next);
      try {
        if (next) localStorage.setItem('garden-almanac', JSON.stringify(next));
        else localStorage.removeItem('garden-almanac');
      } catch {}
    } catch {
      // Reference data only — a failure leaves whatever was already cached in place.
    } finally {
      setAlmanacLoading(false);
    }
  }, []);

  async function saveZone(newZone: number, newZip: string, gardenLocation?: UserGardenLocation | null) {
    if (!guardFreshWrite()) return;
    setZone(newZone);
    setZip(newZip);
    try {
      localStorage.setItem('garden-user-zone', JSON.stringify({
        zone: newZone,
        zip: newZip,
        ...(gardenLocation ? { gardenLocation } : {}),
        setAt: new Date().toISOString(),
      }));
      localStorage.setItem('garden-zone-zip', newZip);
      saveStoredUserGardenLocation(gardenLocation ?? null);
    } catch {}
    try {
      await syncToKV('/api/user/zone', { zone: newZone, zip: newZip, gardenLocation: gardenLocation ?? null });
    } catch {}
    // Drop almanac data for the old zip so the page never shows another location's normals.
    if (almanacData && almanacData.zip !== newZip) {
      setAlmanacData(null);
      try { localStorage.removeItem('garden-almanac'); } catch {}
    }
    void loadAlmanac();

    // Trigger weather reload with new zip
    getWeatherForUser({
      bypassCache: true,
      onGeocodingFailure: () => {
        window.dispatchEvent(new CustomEvent('garden:sync-error', {
          detail: { message: 'Could not find weather for that ZIP code. Check the number and try again.' },
        }));
      },
    })
      .then(w => { if (w) setWeatherData(w); })
      .catch(() => {});
  }

  const refreshAll = useCallback(async () => {
    setIsFresh(false);
    await refreshFromKV();
  }, [refreshFromKV]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production' || typeof window === 'undefined') return;

    window.__gardenDebugPlantingLog = async (logId: string) => {
      const rawLog = plantingLogs.find(log => log.id === logId) ?? null;
      const normalizedResult = rawLog ? normalizePlantingLogs([rawLog]) : null;
      const normalizedLog = normalizedResult?.logs[0] ?? null;

      const [
        plantsModule,
        autoTasksModule,
        guidanceContextModule,
        taskStateModule,
      ] = await Promise.all([
        import('@/data/plants'),
        import('@/lib/autoTasks'),
        import('@/lib/guidance/guidanceContext'),
        import('@/lib/guidance/taskState'),
      ]);

      const plant = normalizedLog
        ? plantsModule.PLANTS.find(candidate => candidate.id === normalizedLog.plantId) ?? null
        : null;
      const today = guidanceContextModule.guidanceAnchorDate(weatherData, undefined);
      const plantLookup = new Map(
        plant && normalizedLog ? [[normalizedLog.plantId, plant]] : [],
      );
      const taskState = toAutoTaskState(tasksRef.current);
      const recurrence = taskStateModule.readTaskRecurrenceState();
      const autoTasks = normalizedLog
        ? autoTasksModule
            .generateAutoTasks([normalizedLog], weatherData, plantLookup)
            .filter(task => task.plantingLogId === normalizedLog.id)
        : [];

      const visibleTasks = autoTasks.map(task => {
        const visible = taskStateModule.shouldShowTask(
          task,
          taskState,
          today,
          { recurrence, surface: 'calendar' },
        );
        const reason = visible
          ? 'visible'
          : guidanceContextModule.isStaleHarvestTask(task, today)
            ? 'stale harvest'
            : taskState.autoChecked.includes(task.id)
              ? 'checked'
              : taskState.autoDismissed.includes(task.id)
                ? 'dismissed or in cooldown'
                : taskState.autoArchived[task.id]
                  ? 'archived or in cooldown'
                  : 'suppressed';

        return {
          id: task.id,
          title: task.shortTitle,
          category: task.category,
          dueDate: autoTasksModule.effectiveDueDate(task),
          priority: task.priority,
          priorityLevel: task.priorityLevel,
          shouldShow: visible,
          suppressionReason: reason,
        };
      });

      const isPlantedRow = Boolean(
        normalizedLog &&
        guidanceContextModule.hasValidPlantedDate(normalizedLog) &&
        normalizedLog.status !== 'finished',
      );

      console.info('[garden:planting-log-debug]', {
        logId,
        rawLog,
        normalizedLog,
        normalizationChanged: normalizedResult?.changed ?? false,
        normalizationRepairs: normalizedResult?.repairs ?? [],
        canonicalPlantFound: Boolean(plant),
        canonicalPlant: plant
          ? {
              id: plant.id,
              name: plant.name,
              lifecycle: plant.lifecycle,
              harvestWindow: plant.harvestWindow ?? null,
            }
          : null,
        activeCareEligible: normalizedLog
          ? guidanceContextModule.shouldGenerateCareTasks(normalizedLog, today)
          : false,
        generatedAutoTasks: visibleTasks,
        calendarShowAllIncluded: isPlantedRow,
        calendarFocusedIncludedApproximation: visibleTasks.some(task => task.shouldShow),
        taskState: {
          autoChecked: taskState.autoChecked,
          autoDismissed: taskState.autoDismissed,
          autoArchived: taskState.autoArchived,
          recurrence,
        },
      });
    };

    return () => {
      delete window.__gardenDebugPlantingLog;
    };
  }, [plantingLogs, tasks, weatherData]);

  // ── Value ─────────────────────────────────────────────────────────────────

  const value: UserData = {
    tasks,
    saveTasks,
    journalEntries,
    saveJournalEntries,
    plantingLogs,
    savePlantingLogs,
    harvestEntries,
    saveHarvestEntries,
    seedInventory,
    saveSeedInventory,
    plannedPlants,
    savePlannedPlants,
    favorites,
    saveFavorites,
    zone,
    zip,
    saveZone,
    weatherData,
    almanacData,
    almanacLoading,
    loadAlmanac,
    isLoading,
    isLoaded,
    isFresh,
    refreshAll,
  };

  return (
    <UserDataContext.Provider value={value}>
      {children}
      {syncError && (
        <div className="fixed right-4 bottom-4 z-[70] max-w-sm rounded-xl border border-[#F5C6C3] bg-white px-4 py-3 shadow-lg">
          <div className="flex items-start gap-3">
            <p className="flex-1 font-body text-[13px] leading-relaxed text-alert-red">
              {syncError}
            </p>
            <button
              type="button"
              onClick={() => setSyncError(null)}
              className="font-body text-[12px] font-semibold text-sage hover:text-dark-text transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
    </UserDataContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useUserData(): UserData {
  const context = useContext(UserDataContext);
  if (!context) throw new Error('useUserData must be used within UserDataProvider');
  return context;
}
